import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import './App.css';
import './RequestCoverage.css';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import awsmobile from './aws-exports.js';

const RequestCoverage = ({ user }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [coverageReason, setCoverageReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Ref to track if a date was just selected to prevent immediate re-opening
  const dateSelectedRef = useRef(false);

  // --- DEBUGGING useEffects (can be removed in production) ---
  useEffect(() => {
    console.log('Start Date Picker Open State:', isStartPickerOpen);
  }, [isStartPickerOpen]);

  useEffect(() => {
    console.log('End Date Picker Open State:', isEndPickerOpen);
  }, [isEndPickerOpen]);
  // --- END DEBUGGING useEffects ---

  const handleStartDateSelect = (date) => {
    console.log('handleStartDateSelect called with:', date);
    setStartDate(date);
    setError('');

    // Calculate the new max allowed end date based on the selected start date
    const newMaxAllowedEndDate = date ? new Date(date.getTime() + (14 * 24 * 60 * 60 * 1000)) : null;

    // Clear end date if start date is after end date OR if end date exceeds the new 14-day limit
    if (endDate && (date > endDate || (newMaxAllowedEndDate && endDate > newMaxAllowedEndDate))) {
      setEndDate(null);
      setError('Start date must precede end date and not exceed 14 days.');
    }

    // Explicitly close the start date picker after selection
    setIsStartPickerOpen(false);
    dateSelectedRef.current = true; // Mark that a date was just selected
    console.log('Attempted to set isStartPickerOpen to false, dateSelectedRef.current set to true');

    // NEW: Automatically open the end date picker if end date is not yet selected
    if (!endDate) {
      setIsEndPickerOpen(true);
      console.log('End date not selected, automatically opening End Date picker.');
    }
  };

  const handleEndDateSelect = (date) => {
    console.log('handleEndDateSelect called with:', date);

    // Calculate the max allowed end date for validation in this handler
    const currentMaxAllowedEndDate = startDate
      ? new Date(startDate.getTime() + (14 * 24 * 60 * 60 * 1000))
      : null;

    console.log('Validation: startDate:', startDate, 'selected date:', date, 'currentMaxAllowedEndDate:', currentMaxAllowedEndDate);

    // --- NEW VALIDATION LOGIC FOR END DATE SELECTION ---
    if (!startDate) { // Ensure a start date is selected first
        setError('Please select a start date first.');
        console.log('Validation: No start date, setting error.');
    } else if (date < startDate) {
      setError('End date must be after the start date.');
      console.log('Validation: End date selected before start date. Setting error.');
    } else if (currentMaxAllowedEndDate && date > currentMaxAllowedEndDate) {
      setError('The selected end date cannot be more than 14 days after the start date.');
      console.log('Validation: Invalid end date selected (beyond 14 days). Setting error.');
      // Do NOT set the endDate if it's invalid. The DatePicker will revert to its previous value.
    } else {
      setEndDate(date); // Only set if valid
      setError(''); // Clear error if valid
      console.log('Validation: Date is valid. Setting endDate and clearing error.');
    }
    // --- END NEW VALIDATION LOGIC ---

    // Explicitly close the end date picker after selection
    setIsEndPickerOpen(false);
    dateSelectedRef.current = true; // Mark that a date was just selected
    console.log('Attempted to set isEndPickerOpen to false, dateSelectedRef.current set to true');
  };

  const handleRequestSubmit = async (event) => {
    event.preventDefault();

    const reason = coverageReason === 'Other' ? customReason : coverageReason;

    if (!startDate || !endDate || !reason) {
      setError('Start date, end date, and coverage reason are required');
      return;
    }

    if (startDate > endDate) {
      setError('Start date must be before the end date');
      return;
    }

    // Add explicit check for 14-day limit on submission
    const maxAllowedEndDateOnSubmit = new Date(startDate.getTime() + (14 * 24 * 60 * 60 * 1000));
    if (endDate > maxAllowedEndDateOnSubmit) {
      setError('The coverage period cannot exceed 14 days from the start date.');
      return;
    }

    if (coverageReason === 'Other' && customReason.length > 100) {
      setError('Custom reason cannot exceed 100 characters');
      return;
    }

    setIsSubmitting(true); // Start loading
    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');
    const requestedBy = user.signInDetails.loginId;
    const cognitoIdToken = localStorage.getItem(`CognitoIdentityServiceProvider.${awsmobile.aws_user_pools_web_client_id}.${user.userId}.idToken`);

    try {
      const urLisa = 'https://qab6yotn8g.execute-api.us-west-1.amazonaws.com/dev-lisa/RequestCoverage';
      const payload = {
        body: {
          request_id: uuidv4(),
          se_email: requestedBy,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
          request_time: Math.floor(Date.now() / 1000).toString(),
          reason: reason
        }
      };

      const response = await fetch(urLisa, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${cognitoIdToken}`
        },
        body: JSON.stringify(payload)
      });

      const url2 = `https://qab6yotn8g.execute-api.us-west-1.amazonaws.com/dev-lisa/RequestNotification?se_name=${requestedBy}&start_date=${formattedStartDate}&end_date=${formattedEndDate}&coverage_reason=${reason}`;
      await fetch(url2, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${cognitoIdToken}`
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(data);

      setSuccess('Request submitted successfully!');
      setError('');

      navigate('/request-submitted');
    } catch (error) {
      setError('Failed to submit request');
      setSuccess('');
      console.error("Error submitting request:", error);
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  // Calculate the maximum allowed end date (startDate + 14 days)
  const maxAllowedEndDate = startDate
    ? new Date(startDate.getTime() + (14 * 24 * 60 * 60 * 1000))
    : null; // If no start date, no max date constraint initially

  // Log maxAllowedEndDate to console for debugging
  useEffect(() => {
    console.log('Calculated maxAllowedEndDate:', maxAllowedEndDate);
  }, [maxAllowedEndDate]);


  return (
    <div className="centered-content">
      <h2 className="title request-title">Coverage Request</h2>
      <form onSubmit={handleRequestSubmit} className="form-container">
        <div className="form-label">
          <p className="title sub-title" style={{ fontStyle: 'italic', margin: 0, padding: 0 }}>
            *Date range must not exceed two weeks
          </p>
        </div>
        <label className="form-label">
          Start Date
          <DatePicker
            key="start-date-picker"
            selected={startDate}
            onSelect={handleStartDateSelect}
            onChange={(date) => setStartDate(date)}
            onClickOutside={() => {
              console.log('onClickOutside for Start Date');
              if (!dateSelectedRef.current) {
                setIsStartPickerOpen(false);
              }
              dateSelectedRef.current = false;
            }}
            open={isStartPickerOpen}
            onInputClick={() => {
              console.log('onInputClick for Start Date');
              if (!dateSelectedRef.current) {
                setIsStartPickerOpen(true);
                setIsEndPickerOpen(false); // Close other picker
              }
              dateSelectedRef.current = false;
            }}
            minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
            dateFormat="MM/dd/yy"
            className="styled-input"
            shouldCloseOnSelect={false}
            dayClassName={(date) => {
              if (date < new Date()) return "past-date";
              if (endDate && date > endDate) return "invalid-date";
              return "";
            }}
          />
        </label>

        <label className="form-label">
          End Date
          <DatePicker
            key="end-date-picker"
            selected={endDate}
            onSelect={handleEndDateSelect}
            onClickOutside={() => {
              console.log('onClickOutside for End Date');
              if (!dateSelectedRef.current) {
                setIsEndPickerOpen(false);
              }
              dateSelectedRef.current = false;
            }}
            open={isEndPickerOpen}
            onInputClick={() => {
              console.log('onInputClick for End Date');
              if (!dateSelectedRef.current) {
                setIsEndPickerOpen(true);
                setIsStartPickerOpen(false); // Close other picker
              }
              dateSelectedRef.current = false;
            }}
            minDate={startDate || new Date(new Date().setDate(new Date().getDate() + 1))}
            maxDate={maxAllowedEndDate}
            dateFormat="MM/dd/yy"
            className="styled-input"
            shouldCloseOnSelect={false}
            dayClassName={(date) => {
              if (date < new Date()) return "past-date";
              if (startDate) {
                const msDiff = date - startDate;
                const diffInDays = msDiff / (1000 * 60 * 60 * 24);
                // This highlights dates 14 days or more past start date
                if (date < startDate || diffInDays > 14) return "invalid-date";
              }
              return "";
            }}
          />
        </label>
        <label className="form-label">
          Reason for Coverage
          <select
            value={coverageReason}
            onChange={(e) => setCoverageReason(e.target.value)}
            required
            className="styled-input"
          >
            <option value="" disabled>Select</option>
            <option value="PTO">PTO</option>
            <option value="Site Walk">Site Walk</option>
            <option value="Territory Trip">Territory Trip</option>
            <option value="VCE">VCE</option>
            <option value="Conference">Conference</option>
            <option value="Other">Other</option>
          </select>
        </label>

        {coverageReason === 'Other' && (
          <label className="form-label">
            Please specify:
            <div className="input-container">
              <input
                type="text"
                value={customReason}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setCustomReason(e.target.value);
                  } else {
                    setError('Custom reason cannot exceed 100 characters');
                  }
                }}
                maxLength="100"
                required
                className="styled-input input-with-counter"
              />
              <span className="character-counter">{customReason.length}/100</span>
            </div>
          </label>
        )}
        <button 
          type="submit" 
          className={`submit-button ${isSubmitting ? "claiming" : ""}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span> Submitting...
            </>
          ) : (
            "Submit"
          )}
        </button>
      </form>
      {error && (
      <p className="error-message shake" key={error}>
        {error}
      </p>
      )}
      {success && <p className="success-message">{success}</p>}
    </div>
  );
};

export default RequestCoverage;
